import _ from 'underscore';
import { getDefaultConfig } from './default-config';
import { LoggingService } from './logging-service';
import { LogTransmitter } from './log-transmitter';
import {
  IActivity,
  IInstrumentation,
  IInstrumentationSchema,
  IListeners,
  ILogger,
  ILoggers,
  ILogTransmitter,
  InstrumentationConfig,
  IProcessors,
  IScenario,
  IWriteRawLog,
} from './types';
import { ScenarioFactory } from './scenario-factory';
import { ActivityFactory } from './activity-factory';

export class Instrumentation implements IInstrumentation {
  isDebugMode!: boolean;

  listeners!: IListeners;

  logTransmitter!: ILogTransmitter;

  loggers!: ILoggers;

  loggingService!: LoggingService;

  processors!: IProcessors;

  scenarioContext: IScenario;

  [key: string]: unknown;

  writeRawLog!: IWriteRawLog;

  /**
   * Create an instance of Instrumentation
   * @param {InstrumentationConfig} config - should contain a config of loggers/processors/listeners:
   * loggers - specify custom loggers to override or add to the default trace, perf, error loggers.
   * processors - specify custom log processors to modify or enrich the logs.
   * listeners - specify listeners to write logs to.
   * isDebugMode - specify if it's in debug mode or not. Default to false. It will throw error
   * if (scenario, activity) pair has already been registered before in debug mode
   */
  constructor(config: InstrumentationConfig = {}) {
    this.setup(config);

    // scenarioContext maintains the current scenario in use.
    this.scenarioContext = {} as IScenario;

    this.initTransmissionService(this.scenarioContext);

    this.initLoggingService();
  }

  setup(config: InstrumentationConfig): void {
    const defaultConfig = getDefaultConfig();
    this.loggers = _.defaults(config.loggers || {}, defaultConfig.loggers);
    this.processors = _.defaults(config.processors || {}, defaultConfig.processors);
    this.listeners = _.defaults(config.listeners || {}, defaultConfig.listeners);
    this.isDebugMode = (config.isDebugMode || defaultConfig.isDebugMode) as boolean;

    window.addEventListener('unload', () => this.stop());
  }

  stop(): void {
    this.beforeStop();
    this.logTransmitter.stop();
  }

  initTransmissionService(scenarioContext: IScenario): void {
    this.logTransmitter = new LogTransmitter({
      scenarioContext,
      processors: this.processors,
      listeners: this.listeners,
    });
    this.writeRawLog = (rawLogs: any) => this.logTransmitter.transmit(rawLogs);
  }

  initLoggingService(): void {
    _.each(this.loggers, (logger: ILogger) => {
      logger.logTransmitter = this.logTransmitter; // eslint-disable-line no-param-reassign
    });
    this.loggingService = new LoggingService({
      loggers: this.loggers,
      isDebugMode: this.isDebugMode,
    });
  }

  /**
   * adds specified scenarios on to the instrumentation instance
   * @param {object} scenarioSchema - schema that specifies the scenarios to be added
   * @returns {Instrumentation} - return self for chaining
   */
  addScenario(scenarioSchema: IInstrumentationSchema): IInstrumentation {
    _.extend(this, this.loggingService.parseSchema(scenarioSchema));
    return this;
  }

  /**
   * add simple scenario and activity for one time use
   * @param {object} scenarioContext - scenario context such as name, parentScenario
   * @returns {object} - activity instance ready to call logger methods
   * usage would be: instrumentation.createScenarioAndActivity("scenario", "activity")
   * DO NOT use this if the scenario is created multiple times, in which case
   * the scenarioId would be different
   *  .signal("something happened")
   */
  createScenarioAndActivity(scenarioContext: IScenario):{
    scenario: IScenario;
    activity: IActivity;
  } {
    const { parentScenario, scenarioName, activityName } = scenarioContext;

    // build the scenario schema with activity
    const schema = {
      scenario: [
        {
          name: scenarioName,
          value: scenarioName,
          activity: activityName ? [
            activityName,
          ] : [],
        },
      ],
    } as IInstrumentationSchema;
    this.addScenario(schema);
    const scenario = (this[scenarioName!] as ScenarioFactory).create(parentScenario);
    const activity = activityName ? (scenario[activityName] as ActivityFactory).create() : undefined;
    return {
      scenario,
      activity: activity!,
    };
  }

  /**
   * add simple scenario and activity for one time use
   * @param {object} scenarioContext - scenario context such as name, parentScenario
   * @returns {object} - scenario instance
   */
  createScenario(scenarioContext: IScenario): IScenario {
    return this.createScenarioAndActivity(scenarioContext).scenario;
  }

  /**
   * method for custom logic to be run before the instrumentation service exits
   * can be overridden
   * @returns {undefined} - no returns
   */
  beforeStop(): void {
    _.noop();
  }

  get ScenarioContext(): Record<string, any> {
    return this.scenarioContext;
  }
}
